import type { Appointment } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMedplumSubscriptionRefresh } from '@/hooks/useMedplumSubscriptionRefresh';
import { buildAppointmentByIdSubscriptionCriteria } from '@/lib/fhirRealtimeCriteria';
import {
  getZoomMeetingFromAppointment,
  type AppointmentZoomMeeting,
} from '@/lib/zoom/appointmentZoomMeeting';
import {
  isZoomMeetingWaitExpired,
  resolveZoomMeetingWaitState,
  type ZoomMeetingWaitState,
} from '../lib/zoomMeetingWaitState';

const POLL_INTERVAL_MS = 2_000;
const MAX_WAIT_MS = 45_000;

export type { ZoomMeetingWaitState };

export interface UseTelehealthZoomMeetingResult {
  appointment: Appointment | undefined;
  loading: boolean;
  zoom: AppointmentZoomMeeting;
  waitState: ZoomMeetingWaitState;
  /** Re-fetch appointment and restart the Zoom setup wait window. */
  checkAgain: () => Promise<void>;
}

/**
 * Loads an Appointment and waits for Zoom meeting extensions (written asynchronously by
 * zoom-meeting-creation bot after calendar create).
 *
 * Polls with `cache: 'no-cache'` so Medplum's in-memory Appointment cache (often populated
 * before the bot writes meeting id/password) cannot keep the UI stuck on "Setting up…".
 */
export function useTelehealthZoomMeeting(
  appointmentId: string | undefined,
): UseTelehealthZoomMeetingResult {
  const medplum = useMedplum();
  const [appointment, setAppointment] = useState<Appointment | undefined>();
  const [loading, setLoading] = useState(true);
  const [waitState, setWaitState] = useState<ZoomMeetingWaitState>('idle');
  const waitStartedAtRef = useRef<number | null>(null);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!appointmentId) {
      setAppointment(undefined);
      setLoading(false);
      setWaitState('idle');
      return;
    }
    if (!options?.silent) {
      setLoading(true);
    }
    try {
      // Bypass Medplum client cache — meeting extensions are written async after schedule.
      const appt = await medplum.readResource('Appointment', appointmentId, { cache: 'no-cache' });
      setAppointment(appt);
    } catch {
      setAppointment(undefined);
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [appointmentId, medplum]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const subscriptionCriteria = useMemo(
    () => (appointmentId ? buildAppointmentByIdSubscriptionCriteria(appointmentId) : undefined),
    [appointmentId],
  );

  useMedplumSubscriptionRefresh(
    subscriptionCriteria,
    () => void reload({ silent: true }),
    { debugLabel: '[TelehealthSession][WS]' },
  );

  const zoom = useMemo(() => getZoomMeetingFromAppointment(appointment), [appointment]);

  useEffect(() => {
    if (loading) {
      return;
    }

    setWaitState((prev) => {
      const next = resolveZoomMeetingWaitState(appointment, prev);
      if (next === 'waiting' && waitStartedAtRef.current == null) {
        waitStartedAtRef.current = Date.now();
      }
      if (next === 'ready' || next === 'not_expected') {
        waitStartedAtRef.current = null;
      }
      return next;
    });
  }, [loading, appointment, zoom.meetingNumber]);

  useEffect(() => {
    if (waitState !== 'waiting' || !appointmentId) return;

    const tick = () => {
      const started = waitStartedAtRef.current ?? Date.now();
      if (isZoomMeetingWaitExpired(started, Date.now(), MAX_WAIT_MS)) {
        setWaitState('timed_out');
        return;
      }
      void reload({ silent: true });
    };

    // Poll immediately — do not wait a full interval after entering "waiting".
    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [waitState, appointmentId, reload]);

  const checkAgain = useCallback(async () => {
    waitStartedAtRef.current = Date.now();
    setWaitState('waiting');
    await reload({ silent: false });
  }, [reload]);

  return { appointment, loading, zoom, waitState, checkAgain };
}
